import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardMedia,
  Divider,
} from "@mui/material";
import {
  PlayArrow,
  CheckCircle,
  MusicNote,
  Visibility,
  Mic,
  Album,
  AccessTime,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { votingService } from "../services/votingService";
import { eventService } from "../services/eventService";
import { songService } from "../services/songService";
import { lineupService } from "../services/lineupService";
import { scheduleService } from "../services/scheduleService";
import { artistImages, eurovisionArtistImages } from "../assets/artistImages";

const ArtistsGridPage = () => {
  const navigate = useNavigate();
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const [eventConfig, setEventConfig] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loadingSongs, setLoadingSongs] = useState(true);
  const imagesBySongKey =
    eventId === "eurovision" ? eurovisionArtistImages : artistImages;
  const isEurovision = eventId === "eurovision";
  const pageTitle = isEurovision
    ? "Eurovision - Artisti"
    : "Sanremo 2026 - Artisti";

  const countryNameToIso2 = React.useMemo(
    () => ({
      Albania: "AL",
      Armenia: "AM",
      Australia: "AU",
      Austria: "AT",
      Azerbaijan: "AZ",
      Belgio: "BE",
      Bulgaria: "BG",
      Croazia: "HR",
      Cipro: "CY",
      "Repubblica Ceca": "CZ",
      Danimarca: "DK",
      Estonia: "EE",
      Finlandia: "FI",
      Francia: "FR",
      Georgia: "GE",
      Germania: "DE",
      Grecia: "GR",
      Israele: "IL",
      Italia: "IT",
      Lettonia: "LV",
      Lituania: "LT",
      Lussemburgo: "LU",
      Malta: "MT",
      Moldavia: "MD",
      Montenegro: "ME",
      Norvegia: "NO",
      Polonia: "PL",
      Portogallo: "PT",
      Romania: "RO",
      "San Marino": "SM",
      Serbia: "RS",
      Svezia: "SE",
      Svizzera: "CH",
      Ucraina: "UA",
      "Regno Unito": "GB",
    }),
    [],
  );

  const getFlagEmoji = React.useCallback(
    (countryName) => {
      if (!countryName) return null;
      const iso2 = countryNameToIso2[countryName];
      if (!iso2 || iso2.length !== 2) return null;
      return iso2
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(127397 + char.charCodeAt(0)),
        );
    },
    [countryNameToIso2],
  );
  const basePath = `/${eventId}`;

  const categoryIcons = {
    canzone: <MusicNote fontSize="small" />,
    presenza_scenica: <Visibility fontSize="small" />,
    esibizione: <Mic fontSize="small" />,
    canzone_cover: <Album fontSize="small" />,
  };
  const { user } = useSelector((state) => state.auth);
  const [activeNightId, setActiveNightId] = useState(1);
  const [viewNightId, setViewNightId] = useState(1);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [lineupOrder, setLineupOrder] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const subEvents = React.useMemo(
    () => (Array.isArray(eventConfig?.subEvents) ? eventConfig.subEvents : []),
    [eventConfig],
  );
  const nights = React.useMemo(() => {
    if (isEurovision) {
      return {
        1: {
          id: 1,
          label: "Finale",
          scheduleKey: "finale",
          isCoverNight: false,
          defaultOrder: null,
          order: 1,
        },
      };
    }
    const byId = {};
    subEvents.forEach((se) => {
      const id = Number(se.id);
      if (!Number.isFinite(id)) return;
      byId[id] = {
        id,
        label: se.label || `Serata ${id}`,
        scheduleKey:
          se.scheduleKey ||
          String(se.label || `serata_${id}`)
            .toLowerCase()
            .replace(" ", "_"),
        isCoverNight: Boolean(se.isCoverNight),
        defaultOrder: Array.isArray(se.defaultOrder) ? se.defaultOrder : null,
        order: Number.isFinite(Number(se.order)) ? Number(se.order) : id,
      };
    });
    return byId;
  }, [subEvents, isEurovision]);
  const orderedNights = React.useMemo(() => {
    return Object.values(nights).sort(
      (a, b) => Number(a.order) - Number(b.order),
    );
  }, [nights]);
  const fallbackNightId = orderedNights[0]?.id || 1;

  const categories = React.useMemo(() => {
    const remote = Array.isArray(eventConfig?.categories)
      ? eventConfig.categories
      : [];
    return remote
      .map((c, index) => ({
        key: String(c.key || "").trim(),
        label: String(c.label || "").trim(),
        max: Number.isFinite(Number(c.max)) ? Number(c.max) : 10,
        order: Number.isFinite(Number(c.order)) ? Number(c.order) : index + 1,
      }))
      .filter((c) => c.key)
      .sort((a, b) => Number(a.order) - Number(b.order));
  }, [eventConfig]);
  const effectiveViewNightId = nights[viewNightId]
    ? viewNightId
    : fallbackNightId;

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Load settings
  useEffect(() => {
    const unsubscribe = eventService.subscribeToEvent(
      eventId,
      (ev) => {
        setEventConfig(ev || null);
        if (isEurovision) {
          setActiveNightId(1);
          setViewNightId(1);
        } else {
          const nightId = Number(ev?.currentSubEventId);
          if (Number.isFinite(nightId)) {
            setActiveNightId(nightId);
            setViewNightId((prev) => (prev === 1 ? nightId : prev));
          }
        }
        setLoadingSettings(false);
      },
      (error) => {
        console.warn("Failed to load event config, using default:", error);
        setEventConfig(null);
        if (isEurovision) {
          setActiveNightId(1);
          setViewNightId(1);
        }
        setLoadingSettings(false);
      },
    );
    return () => unsubscribe();
  }, [eventId, isEurovision]);

  useEffect(() => {
    setLoadingSongs(true);
    const unsubscribe = songService.subscribeToSongs(
      eventId,
      (items) => {
        const normalized = (items || []).map((s) => {
          const baseLabel =
            s?.label ||
            `${s?.artist || ""}${s?.artist && s?.title ? " - " : ""}${s?.title || ""}`.trim() ||
            s?.key;
          return {
            ...s,
            label: baseLabel,
            artist: s?.artist || baseLabel,
            title: s?.title || "",
          };
        });
        setSongs(normalized);
        setLoadingSongs(false);
      },
      (e) => {
        console.warn("Failed to load songs:", e);
        setSongs([]);
        setLoadingSongs(false);
      },
    );
    return () => unsubscribe();
  }, [eventId]);

  useEffect(() => {
    if (!nights[activeNightId]) {
      setActiveNightId(fallbackNightId);
    }
    if (!nights[viewNightId]) {
      setViewNightId(fallbackNightId);
    }
  }, [eventId, nights, activeNightId, viewNightId, fallbackNightId]);

  useEffect(() => {
    if (!isEurovision) {
      const unsubscribe = lineupService.subscribeToLineup(
        effectiveViewNightId,
        (lineup) => {
          setLineupOrder(lineup);
        },
        undefined,
        eventId,
      );
      return () => unsubscribe();
    }

    const primaryNightId = 1;
    const fallbackNightId = 3;
    let fallbackUnsub = null;

    const primaryUnsub = lineupService.subscribeToLineup(
      primaryNightId,
      (lineup) => {
        if (Array.isArray(lineup) && lineup.length > 0) {
          if (fallbackUnsub) {
            fallbackUnsub();
            fallbackUnsub = null;
          }
          setLineupOrder(lineup);
          return;
        }

        if (!fallbackUnsub) {
          fallbackUnsub = lineupService.subscribeToLineup(
            fallbackNightId,
            (fallbackLineup) => setLineupOrder(fallbackLineup),
            undefined,
            eventId,
          );
        }
      },
      undefined,
      eventId,
    );

    return () => {
      primaryUnsub();
      if (fallbackUnsub) fallbackUnsub();
    };
  }, [effectiveViewNightId, eventId, isEurovision]);

  useEffect(() => {
    if (isEurovision) {
      setSchedule(null);
      return;
    }
    const nightKey =
      nights[effectiveViewNightId]?.scheduleKey ||
      nights[effectiveViewNightId]?.label?.toLowerCase().replace(" ", "_");

    if (!nightKey) {
      setSchedule(null);
      return;
    }

    const unsubscribe = scheduleService.subscribeToSchedule(
      effectiveViewNightId,
      nightKey,
      (data) => {
        setSchedule(data);
      },
      eventId,
    );
    return () => unsubscribe();
  }, [effectiveViewNightId, eventId, nights, isEurovision]);

  const handleViewNightChange = (event) => {
    setViewNightId(parseInt(event.target.value, 10));
  };

  // Carica i voti dell'utente dal database
  const { data: userVotesData, isLoading } = useQuery({
    queryKey: ["userVotes", eventId, user?.uid],
    queryFn: () => votingService.getUserVotes(user?.uid, eventId),
    enabled: !!user?.uid,
  });

  const userVotes = userVotesData?.votes || {};
  const hasVoted = !!userVotesData;

  const getSongScore = (song) => {
    if (!userVotes) return 0;
    return categories.reduce((total, cat) => {
      // Skip cover category if not cover night
      if (cat.key === "canzone_cover" && !currentNight?.isCoverNight) {
        return total;
      }

      let voteKey;
      if (cat.key === "canzone") {
        voteKey = `${song.key}_${cat.key}`;
      } else {
        voteKey = `${song.key}_${effectiveViewNightId}_${cat.key}`;
      }
      return total + (userVotes[voteKey] || 0);
    }, 0);
  };

  const isSongCompleted = (song) => {
    if (!userVotes) return false;
    return categories.every((cat) => {
      // Skip cover category if not cover night
      if (cat.key === "canzone_cover" && !currentNight?.isCoverNight) {
        return true;
      }

      let voteKey;
      if (cat.key === "canzone") {
        voteKey = `${song.key}_${cat.key}`;
      } else {
        voteKey = `${song.key}_${effectiveViewNightId}_${cat.key}`;
      }
      const vote = userVotes[voteKey];
      return vote !== undefined && vote !== null && vote >= 1 && vote <= 10;
    });
  };

  const handleSongClick = (songKey) => {
    navigate(
      `${basePath}/voting?song=${encodeURIComponent(songKey)}&night=${effectiveViewNightId}`,
    );
  };

  const handleStartVoting = () => {
    navigate(`${basePath}/voting?night=${effectiveViewNightId}`);
  };

  const currentNight = nights[effectiveViewNightId];

  const sortedSongs = React.useMemo(() => {
    if (Array.isArray(lineupOrder)) {
      const byKey = new Map(songs.map((s) => [s.key, s]));
      return lineupOrder.map((k) => byKey.get(k)).filter(Boolean);
    }

    // Fallback: usa scaletta oraria se disponibile
    if (schedule && schedule.scaletta) {
      const scheduleOrder = [];
      schedule.scaletta.forEach((ev) => {
        if (ev.key) {
          scheduleOrder.push(ev.key);
        } else if (ev.artist) {
          const found = songs.find(
            (s) =>
              s.artist.toUpperCase() === ev.artist.toUpperCase() ||
              (s.cover?.title &&
                ev.evento?.toUpperCase().includes(s.cover.title.toUpperCase())),
          );
          if (found) scheduleOrder.push(found.key);
        }
      });
      const byKey = new Map(songs.map((s) => [s.key, s]));
      return scheduleOrder.map((k) => byKey.get(k)).filter(Boolean);
    }

    // Ultimo fallback: tutti i segmenti ordinati per 'order'
    return [...songs]
      .filter((s) => s && s.key)
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  }, [lineupOrder, schedule, songs]);

  const groupedSongs = React.useMemo(() => {
    const upcoming = [];
    const past = [];

    sortedSongs.forEach((song) => {
      let isPast = false;
      if (schedule && schedule.scaletta) {
        const event = schedule.scaletta.find((e) => e.key === song.key);
        if (event) {
          const offset = schedule.timeOffset || 0;
          const endTime =
            event.endTime instanceof Date
              ? event.endTime
              : new Date(event.endTime);
          const adjustedEnd = new Date(endTime.getTime() - offset * 60000);

          if (currentTime >= adjustedEnd) {
            isPast = true;
          }
        }
      }

      if (isPast) {
        past.push(song);
      } else {
        upcoming.push(song);
      }
    });

    return { upcoming, past };
  }, [sortedSongs, schedule, currentTime]);

  const renderSongGrid = (songsList) => (
    <Grid container spacing={3}>
      {songsList.map((song) => {
        const songScore = getSongScore(song);
        const isCompleted = isSongCompleted(song);

        const { timeLabel, isLive } = (() => {
          if (!schedule || !schedule.scaletta)
            return { timeLabel: null, isLive: false };
          const event = schedule.scaletta.find((e) => e.key === song.key);
          if (!event) return { timeLabel: null, isLive: false };

          let start =
            event.startTime instanceof Date
              ? event.startTime
              : new Date(event.startTime);
          let end =
            event.endTime instanceof Date
              ? event.endTime
              : new Date(event.endTime);

          const offset = schedule.timeOffset || 0;
          const adjustedStart = new Date(start.getTime() - offset * 60000);
          const adjustedEnd = new Date(end.getTime() - offset * 60000);

          const isLive =
            currentTime >= adjustedStart && currentTime < adjustedEnd;

          const timeLabel = adjustedStart.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });

          return { timeLabel, isLive };
        })();

        const flagEmoji = isEurovision ? getFlagEmoji(song.country) : null;

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} key={song.key}>
            <Card
              elevation={isLive ? 8 : 2}
              sx={{
                height: "100%",
                border: isLive
                  ? "2px solid"
                  : isCompleted
                    ? "2px solid"
                    : "1px solid",
                borderColor: isLive
                  ? "secondary.main"
                  : isCompleted
                    ? "success.main"
                    : "grey.300",
                bgcolor: isLive
                  ? "secondary.50"
                  : isCompleted
                    ? "success.50"
                    : "background.paper",
                transition: "all 0.3s ease",
                transform: isLive ? "scale(1.02)" : "none",
                "&:hover": {
                  transform: isLive ? "scale(1.04)" : "translateY(-4px)",
                  boxShadow: isLive ? 12 : 4,
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSongClick(song.key)}
                sx={{
                  height: "100%",
                  p: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                <CardMedia
                  component="img"
                  height="300"
                  image={
                    song.imageUrl ||
                    imagesBySongKey[song.key] ||
                    song.image ||
                    "/placeholder.jpg"
                  }
                  alt={song.artist}
                  sx={{ objectFit: "cover", objectPosition: "top" }}
                />
                <CardContent
                  sx={{
                    p: 2,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    boxSizing: "border-box",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    mb={1}
                  >
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {!isEurovision && timeLabel && (
                        <Chip
                          icon={
                            <AccessTime sx={{ fontSize: "1rem !important" }} />
                          }
                          label={isLive ? "LIVE" : timeLabel}
                          size="small"
                          variant={isLive ? "filled" : "outlined"}
                          color={isLive ? "secondary" : "primary"}
                          sx={{
                            height: 24,
                            alignSelf: "flex-start",
                            animation: isLive ? "pulse 2s infinite" : "none",
                            "@keyframes pulse": {
                              "0%": {
                                boxShadow: "0 0 0 0 rgba(220, 0, 78, 0.7)",
                              },
                              "70%": {
                                boxShadow: "0 0 0 10px rgba(220, 0, 78, 0)",
                              },
                              "100%": {
                                boxShadow: "0 0 0 0 rgba(220, 0, 78, 0)",
                              },
                            },
                          }}
                        />
                      )}
                      {isEurovision && flagEmoji && (
                        <Chip
                          label={flagEmoji}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 24,
                            alignSelf: "flex-start",
                            fontSize: "1rem",
                            px: 0.5,
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                          title={song.country}
                        />
                      )}
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        component="div"
                        sx={{ lineHeight: 1.2 }}
                      >
                        {song.artist}
                      </Typography>
                    </Box>
                    {isCompleted && (
                      <CheckCircle
                        color="success"
                        sx={{ fontSize: 24, ml: 1, flexShrink: 0 }}
                      />
                    )}
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={2}
                    sx={{ flexGrow: 1 }}
                  >
                    {currentNight?.isCoverNight ? (
                      <>
                        <span style={{ fontWeight: "bold" }}>COVER:</span> "
                        {song.cover?.title}"
                        {song.cover?.guest && (
                          <span
                            style={{ display: "block", fontSize: "0.85em" }}
                          >
                            con {song.cover.guest}
                          </span>
                        )}
                      </>
                    ) : (
                      `"${song.title}"`
                    )}
                  </Typography>

                  {userVotes && (
                    <Box mt="auto">
                      <Typography
                        variant="h6"
                        color={isCompleted ? "success.main" : "text.secondary"}
                        textAlign="center"
                      >
                        {isCompleted
                          ? `${songScore}/${
                              currentNight?.isCoverNight ? 25 : 20
                            } punti`
                          : "Non votato"}
                      </Typography>

                      {isCompleted && (
                        <Box
                          mt={1}
                          display="flex"
                          flexWrap="wrap"
                          gap={0.5}
                          justifyContent="center"
                        >
                          {categories.map((cat) => {
                            if (
                              cat.key === "canzone_cover" &&
                              !currentNight?.isCoverNight
                            ) {
                              return null;
                            }

                            let voteKey;
                            if (cat.key === "canzone") {
                              voteKey = `${song.key}_${cat.key}`;
                            } else {
                              voteKey = `${song.key}_${viewNightId}_${cat.key}`;
                            }

                            return (
                              <Chip
                                key={cat.key}
                                icon={categoryIcons[cat.key]}
                                label={userVotes[voteKey] || 0}
                                size="small"
                                sx={{
                                  bgcolor: cat.color,
                                  color: "white",
                                  minWidth: "24px",
                                  fontSize: "0.75rem",
                                  "& .MuiChip-icon": {
                                    color: "white",
                                    fontSize: "1rem",
                                    ml: 0.5,
                                  },
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  if (isLoading || loadingSettings || loadingSongs) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 1400, mx: "auto", mt: 4 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" gutterBottom>
          {pageTitle}
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap={3}
          mb={4}
        >
          {/* View Selector - Available to everyone */}
          {!isEurovision && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Visualizza Serata</InputLabel>
              <Select
                value={viewNightId}
                label="Visualizza Serata"
                onChange={handleViewNightChange}
              >
                {orderedNights.map((night) => (
                  <MenuItem key={night.id} value={night.id}>
                    {night.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Admin Active Night Selector */}
        </Box>

        <Typography variant="body1" color="text.secondary" mb={3}>
          Stai visualizzando: <strong>{currentNight?.label}</strong>.{" "}
          {isEurovision
            ? "Puoi votare per la finale."
            : activeNightId === viewNightId
              ? "Puoi votare per questa serata."
              : "Votazione chiusa per i voti di serata (solo Canzone modificabile)."}
        </Typography>

        {!hasVoted && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayArrow />}
            onClick={handleStartVoting}
            size="large"
            sx={{ mb: 3 }}
          >
            Inizia Votazione Sequenziale
          </Button>
        )}
      </Box>

      {groupedSongs.upcoming.length > 0 &&
        renderSongGrid(groupedSongs.upcoming)}

      {groupedSongs.past.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" gutterBottom>
            Esibizioni precedenti
          </Typography>
          {renderSongGrid(groupedSongs.past)}
        </>
      )}

      {userVotes && (
        <Box mt={4} textAlign="center">
          <Typography variant="h5" color="primary">
            Punteggio Totale Serata:{" "}
            {sortedSongs.reduce((total, song) => total + getSongScore(song), 0)}
            /{sortedSongs.length * (currentNight?.isCoverNight ? 25 : 20)}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Canzoni completate:{" "}
            {sortedSongs.filter((song) => isSongCompleted(song)).length}/
            {sortedSongs.length}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ArtistsGridPage;
